import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import type { TFunction } from 'i18next';
import type { ReservationBoard } from '../../services/reservation-system/reservation-board.service';
import {
  addMsToRsLocalDateTime,
  ensureRsEndAfterStart,
  getRsBookingDurationMinutes,
  getRsCreateBookingFormErrorKey,
  moveRsBookingStartKeepingDuration,
  moveRsBookingStartToNowKeepingDuration,
  nudgeRsBookingEnd,
  RS_DEFAULT_BOOKING_DURATION_MS,
  RS_MIN_BOOKING_DURATION_MS,
  shiftRsBookingRangeByMs,
} from '../../utils/reservationBoardCalendar.util';
import ReservationFormLabel from './ReservationFormLabel';
import RsDateTimeFieldGroup, { RsTimeNudgeButton } from './RsDateTimeFieldGroup';
import {
  RsBookingSidebarNotice,
  RsBookingSidebarOptional,
} from './RsBookingSidebarExtras';
import RsBookingSubmitBar, {
  RS_BOOKING_DISPLAY_NAME_INPUT_ID,
} from './RsBookingSubmitBar';
import styles from './ReservationBoardCalendar.module.css';
import { useRsNarrowBoardLayout } from '../../hooks/useRsCalendarStickyOffsets';

export { RS_BOOKING_DISPLAY_NAME_INPUT_ID };

type RsBookingSidebarFormProps = {
  board: ReservationBoard;
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  startDateTime: string;
  endDateTime: string;
  onStartDateTimeChange: (value: string) => void;
  onEndDateTimeChange: (value: string) => void;
  selectedResourceNames: string[];
  onSelectedResourceNamesChange: (value: string[]) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  deletePassword: string;
  onDeletePasswordChange: (value: string) => void;
  onPersistFormPrefs: () => void;
  onSubmit: () => void;
  onValidationError: (message: string) => void;
  isSubmitting: boolean;
  /** 既存予約と重なる選択項目（登録前に警告） */
  overlapResourceNames?: string[];
  viewYear: number;
  viewMonth: number;
  onRevealSelectionMonth: () => void;
  locale: string;
  t: TFunction;
};

export default function RsBookingSidebarForm({
  board,
  displayName,
  onDisplayNameChange,
  startDateTime,
  endDateTime,
  onStartDateTimeChange,
  onEndDateTimeChange,
  selectedResourceNames,
  onSelectedResourceNamesChange,
  notes,
  onNotesChange,
  deletePassword,
  onDeletePasswordChange,
  onPersistFormPrefs,
  onSubmit,
  onValidationError,
  isSubmitting,
  overlapResourceNames = [],
  viewYear,
  viewMonth,
  onRevealSelectionMonth,
  locale,
  t,
}: RsBookingSidebarFormProps) {
  // step=30分と揃える（+1分 min はネイティブ検証で全候補を invalid にしうる）
  const endDateTimeMin = startDateTime
    ? addMsToRsLocalDateTime(startDateTime, RS_MIN_BOOKING_DURATION_MS)
    : undefined;
  // 既定は閉じる（prefs に備考があっても主経路の縦を食わない）。空→非空のときだけ自動展開
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [summaryPulse, setSummaryPulse] = useState(false);
  const prevNotesRef = useRef(notes);
  const skipSummaryPulseRef = useRef(true);
  const durationMinutes = getRsBookingDurationMinutes(startDateTime, endDateTime);
  const selectionKey = `${selectedResourceNames.join('\u0001')}|${startDateTime}|${endDateTime}`;

  useEffect(() => {
    const prev = prevNotesRef.current;
    prevNotesRef.current = notes;
    if (!prev.trim() && notes.trim()) {
      setOptionalOpen(true);
    }
  }, [notes]);

  // 狭い画面: カレンダー操作で下端要約が変わったことを一瞬ハイライト
  useEffect(() => {
    if (skipSummaryPulseRef.current) {
      skipSummaryPulseRef.current = false;
      return;
    }
    setSummaryPulse(true);
    const timer = window.setTimeout(() => setSummaryPulse(false), 500);
    return () => window.clearTimeout(timer);
  }, [selectionKey]);

  const handleStartChange = (value: string) => {
    const next = moveRsBookingStartKeepingDuration(startDateTime, endDateTime, value);
    onStartDateTimeChange(next.start);
    onEndDateTimeChange(next.end);
  };

  const handleStartNudge = (deltaMs: number) => {
    const next = shiftRsBookingRangeByMs(startDateTime, endDateTime, deltaMs);
    onStartDateTimeChange(next.start);
    onEndDateTimeChange(next.end);
  };

  const handleStartNow = () => {
    const next = moveRsBookingStartToNowKeepingDuration(startDateTime, endDateTime);
    onStartDateTimeChange(next.start);
    onEndDateTimeChange(next.end);
  };

  const handleEndNudge = (deltaMs: number) => {
    onEndDateTimeChange(nudgeRsBookingEnd(startDateTime, endDateTime, deltaMs));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errorKey = getRsCreateBookingFormErrorKey({
      startDateTime,
      endDateTime,
      selectedResourceNames,
      displayName,
    });
    if (errorKey) {
      onValidationError(t(errorKey.key, { defaultValue: errorKey.defaultValue }));
      return;
    }
    onSubmit();
  };

  const minus30 = t('reservation.board.nudgeMinus30', { defaultValue: '−30分' });
  const plus30 = t('reservation.board.nudgePlus30', { defaultValue: '+30分' });
  const durationOneHour = t('reservation.board.durationOneHour', { defaultValue: '1時間' });
  const narrowLayout = useRsNarrowBoardLayout();

  const durationLabel =
    durationMinutes % 60 === 0
      ? t('reservation.board.durationHours', {
          defaultValue: '枠 {{count}}時間',
          count: durationMinutes / 60,
        })
      : t('reservation.board.durationMinutes', {
          defaultValue: '枠 {{count}}分',
          count: durationMinutes,
        });

  return (
    <Form className={styles.sidebarForm} onSubmit={handleSubmit}>
      <div className={styles.sidebarScroll}>
        <h5 className={narrowLayout ? 'visually-hidden' : styles.sidebarFormTitle}>
          {t('reservation.board.formTitle', { defaultValue: '登録フォーム' })}
        </h5>
        {/* お願い・連絡先は上部へ（フォーム末尾だと狭い画面で固定登録バーに隠れタップ不能） */}
        <RsBookingSidebarNotice
          board={board}
          open={noticeOpen}
          onOpenChange={setNoticeOpen}
          t={t}
        />
        <Form.Group className="mb-2">
          <ReservationFormLabel
            label={t('reservation.board.displayName', { defaultValue: '所属・名前' })}
            t={t}
          />
          <Form.Control
            id={RS_BOOKING_DISPLAY_NAME_INPUT_ID}
            value={displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
            onBlur={onPersistFormPrefs}
            size="sm"
            required
            placeholder={t('reservation.board.displayNamePlaceholder', {
              defaultValue: '例: 山田太郎',
            })}
            autoComplete="name"
          />
        </Form.Group>
        <RsDateTimeFieldGroup
          label={t('reservation.board.startDateTime', { defaultValue: '開始日時' })}
          value={startDateTime}
          onChange={handleStartChange}
          locale={locale}
          t={t}
          nudgeAriaLabel={t('reservation.board.timeNudgeStart', {
            defaultValue: '開始を30分単位で調整（終了も同じだけずれます）',
          })}
          nudges={
            <>
              <RsTimeNudgeButton
                onClick={() => handleStartNudge(-RS_MIN_BOOKING_DURATION_MS)}
                title={minus30}
                aria-label={minus30}
              >
                −30
              </RsTimeNudgeButton>
              <RsTimeNudgeButton
                onClick={() => handleStartNudge(RS_MIN_BOOKING_DURATION_MS)}
                title={plus30}
                aria-label={plus30}
              >
                +30
              </RsTimeNudgeButton>
              <RsTimeNudgeButton
                variant="outline-primary"
                title={t('reservation.board.startNowHint', {
                  defaultValue: '開始をいまの次の枠へ（終了も同じだけずれます）',
                })}
                aria-label={t('reservation.board.startNow', { defaultValue: 'いま' })}
                onClick={handleStartNow}
              >
                {t('reservation.board.startNow', { defaultValue: 'いま' })}
              </RsTimeNudgeButton>
            </>
          }
        />
        <RsDateTimeFieldGroup
          label={t('reservation.board.endDateTime', { defaultValue: '終了日時' })}
          value={endDateTime}
          onChange={(value) => onEndDateTimeChange(ensureRsEndAfterStart(startDateTime, value))}
          min={endDateTimeMin}
          locale={locale}
          t={t}
          includeOvernightContinuation
          labelMeta={durationLabel}
          nudgeAriaLabel={t('reservation.board.timeNudgeEnd', {
            defaultValue: '終了を30分単位で調整',
          })}
          nudges={
            <>
              <RsTimeNudgeButton
                onClick={() => handleEndNudge(-RS_MIN_BOOKING_DURATION_MS)}
                title={minus30}
                aria-label={minus30}
              >
                −30
              </RsTimeNudgeButton>
              <RsTimeNudgeButton
                onClick={() => handleEndNudge(RS_MIN_BOOKING_DURATION_MS)}
                title={plus30}
                aria-label={plus30}
              >
                +30
              </RsTimeNudgeButton>
              <RsTimeNudgeButton
                variant="outline-primary"
                title={t('reservation.board.durationOneHourHint', {
                  defaultValue: '終了を開始の1時間後に設定',
                })}
                aria-label={durationOneHour}
                onClick={() =>
                  onEndDateTimeChange(
                    addMsToRsLocalDateTime(startDateTime, RS_DEFAULT_BOOKING_DURATION_MS)
                  )
                }
              >
                {durationOneHour}
              </RsTimeNudgeButton>
            </>
          }
        />
        <Form.Group
          className="mb-2"
          title={
            board.resourceNames.length > 1
              ? t('reservation.board.multiResourceHintShort', {
                  defaultValue: '複数選択可（同じ時間帯でまとめて予約）',
                })
              : undefined
          }
        >
          <ReservationFormLabel
            label={t('reservation.board.resource', { defaultValue: '項目' })}
            t={t}
          />
          <div className={styles.resourceCheckList} data-rs-resource-list>
            {board.resourceNames.map((name, resourceIndex) => (
              <Form.Check
                key={name}
                type="checkbox"
                className={styles.resourceCheck}
                // 項目名を id に載せない（空白・記号でラベル関連が壊れるのを避ける）
                id={`reservation-resource-${board.id}-${resourceIndex}`}
                label={name}
                checked={selectedResourceNames.includes(name)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selectedResourceNames, name]
                    : selectedResourceNames.filter((item) => item !== name);
                  onSelectedResourceNamesChange(next);
                }}
              />
            ))}
          </div>
        </Form.Group>
        <RsBookingSidebarOptional
          notes={notes}
          onNotesChange={onNotesChange}
          deletePassword={deletePassword}
          onDeletePasswordChange={onDeletePasswordChange}
          onPersistFormPrefs={onPersistFormPrefs}
          open={optionalOpen}
          onOpenChange={setOptionalOpen}
          t={t}
        />
        {overlapResourceNames.length > 0 ? (
          <p className={styles.overlapHint} role="alert">
            {t('reservation.board.overlapHint', {
              defaultValue:
                '既存の予約と時間が重なっています: {{names}}。時間か項目を変えてください。',
              names: overlapResourceNames.join('・'),
            })}
          </p>
        ) : null}
      </div>
      <RsBookingSubmitBar
        displayName={displayName}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        selectedResourceNames={selectedResourceNames}
        overlapResourceNames={overlapResourceNames}
        isSubmitting={isSubmitting}
        summaryPulse={summaryPulse}
        viewYear={viewYear}
        viewMonth={viewMonth}
        onRevealSelectionMonth={onRevealSelectionMonth}
        t={t}
      />
    </Form>
  );
}
